<template>
	<div
		class="hdl-ranking question"
		:aria-labelledby="`${question.uuid}-label`"
		:id="`${question.uuid}-container`"
		:class="{
			'has-error': errorMap.size > 0
		}"
		@keydown="keydownHandler"
		v-show="question.visible"
	>
		<fieldset ref="container">
			<legend>
				<div
					ref="rendered"
					v-html="question.renderedQuestion"
				/>
			</legend>

			<div class="errors" :id="`errs-${question.uuid}`" v-if="errorMessages.length" aria-live="assertive">
				<ul>
					<li v-for="errorMessage of errorMessages" :key="errorMessage">
						{{ errorMessage }}
					</li>
				</ul>
			</div>

			<div class="scale-label top" v-if="topLabel">
				{{ topLabel }}
			</div>
			<Container
				:should-accept-drop="shouldAcceptDrop"
				bar-class="hdl-ranking-drag-bar"
				@drop="choiceDropped"
				class="choices"
			>
				<Draggable
					v-for="choice, index of choices"
					class="choice"
					ref="choices"
					:key="choice.uuid"
					:get-payload="() => ({
						type: 'question:choice',
						uuid: choice.uuid,
						questionUuid: question.uuid
					})"
					drag-handle-selector=".rank-indicator"
					drag-class="hdl-ranking-choice-dragging"
					:data-uuid="choice.uuid"
					:data-index="choice.index"
					:drag-delay="200"
					@click.prevent.stop="selectChoice(choice.uuid)"
				>
					<div
						class="mr-2 flex-none flex flex-row items-center radio-container"
					>
						<div class="rank-indicator" :class="{'selected': getChoiceRank(choice.uuid) !== null}">
							<font-awesome-icon :icon="['far', 'arrows-from-line']" v-if="getChoiceRank(choice.uuid) === null"/>
							<div v-else>{{ getChoiceRank(choice.uuid) }}</div>
							<input
								type="checkbox"
								:name="question.uuid"
								:id="choice.uuid"
								:aria-labelledby="`${choice.uuid}-label`"
								:checked="selectedChoices.includes(choice.uuid)"
								@change="selectChoice(choice.uuid)"
							/>
						</div>
					</div>
					<label
						class="flex flex-col flex-1"
						:id="`${choice.uuid}-label`"
						:for="choice.uuid"
					>
						<div class="choice-label">{{ choice.label }}</div>
					</label>
				</Draggable>
			</Container>
			<div class="scale-label" v-if="bottomLabel">
				{{ bottomLabel }}
			</div>
		</fieldset>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { PublishedQuestionChoiceType, PublishedSurveyQuestionType, QuestionChoiceBadge, QuestionOptionKey, SurveyResponseErrorCode, SurveyResponseInputErrorType } from '@/common/interfaces';

import { IQuestionComponent } from './types';
import { ResponseGroup } from '../../responses';
import { useResponseStore } from '@/respondants/stores/responseStore';
import { inPlaceShuffleArray } from '@/common/utils';
import { faArrowsFromLine } from '@fortawesome/pro-regular-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { Draggable } from '@/components/common/dnd/draggable';
import { Draggable as DraggableComponent } from '@/components/common/dnd/Draggable.vue';
import { Container } from '@/components/common/dnd/container';

library.add(faArrowsFromLine);

declare type QuestionChoiceDragPayload = {
	uuid: string;
	questionUuid: string;
	type: 'question:choice';
};

@Component({
	emits: ['intentionalBlur']
})
export default class Ranking extends Vue implements IQuestionComponent {
	@Prop({ required: true })
	question: PublishedSurveyQuestionType;

	response: Array<string> | null;

	selectedChoices: Array<string> = [];

	errorMap: Map<SurveyResponseErrorCode, string> = new Map();

	mounted(): void {
		this.$el.__vue__ = this;

		this.errorMap = new Map();

		this.selectedChoices = [];

		if (this.responseGroup) {
			const existingResponse = this.responseGroup.getResponse(this.question.uuid);
			if (existingResponse && existingResponse.choices && existingResponse.choices.length) {
				const choices = [...existingResponse.choices.filter((c) => c.rank !== undefined && c.rank !== null)];
				choices.sort((a, b) => a.rank! - b.rank!);
				for (const existingSelectedChoice of choices) {
					this.selectedChoices.push(existingSelectedChoice.choiceUuid);
				}
			}
		}

		this.accessorizeRendered();
	}

	getChoiceRank(choiceUuid: string): number | null {
		const index = this.selectedChoices.indexOf(choiceUuid);
		if (index === -1) {
			return null;
		}

		return index + 1;
	}

	moveChoice(choiceUuid: string, toIndex: number): void {
		const currentChoiceUuidOrder = [...this.choices.map((c) => c.uuid)];

		const choiceIndexCurrent = currentChoiceUuidOrder.indexOf(choiceUuid);
		if (choiceIndexCurrent < 0) {
			return;
		}

		const result: Array<string> = [];
		let i = 0;
		for (const cUuid of currentChoiceUuidOrder) {
			if (cUuid === choiceUuid) {
				continue;
			}

			if (i === toIndex) {
				result.push(choiceUuid);
			}

			result.push(cUuid);
			i++;
		}

		if (toIndex === this.choices.length - 1) {
			result.push(choiceUuid);
		}

		this.selectedChoices = result;

		this.setResponse();
	}

	selectChoice(choiceUuid: string, focus: boolean = true): void {
		const index = this.selectedChoices.indexOf(choiceUuid);
		if (index > -1) {
			this.selectedChoices.splice(index, 1);
		} else {
			this.selectedChoices.push(choiceUuid);

			if (this.choices.length - this.selectedChoices.length < 2) {
				// If there is only 1 choice left, auto place it
				for (const choiceToSelect of this.choices) {
					if (!this.selectedChoices.includes(choiceToSelect.uuid)) {
						this.selectChoice(choiceToSelect.uuid, false);
					}
				}
			}
		}

		if (focus) {
			this.$nextTick(() => {
				const input = (this.$refs.container as HTMLFieldSetElement).querySelector(`.choice[data-uuid="${choiceUuid}"] input[type="checkbox"]`) as HTMLInputElement | null;
				if (input) {
					input.focus();
				}
			});
		}

		this.setResponse();
	}

	focusFrom(fromDirection: 'above' | 'below'): boolean {
		const choices = this.sortedChoiceEls;

		if (choices === undefined) {
			return true;
		}

		if (fromDirection === 'below' && choices.length > 0) {
			choices[this.choices.length - 1].focus();
		} else if (choices.length > 0) {
			choices[0].focus();
		}

		return true;
	}

	keydownHandler(event: KeyboardEvent): void {
		const focusedUuid = this.focusedDomChoiceUuid();
		const shiftPressed = event.shiftKey;

		if (event.key === 'ArrowUp') {
			event.stopPropagation();
			event.preventDefault();

			if (shiftPressed && focusedUuid) {
				const existingChoiceIndex = this.choices.findIndex((c) => c.uuid === focusedUuid);
				if (existingChoiceIndex > 0) {
					this.moveChoice(focusedUuid, existingChoiceIndex-1);
					this.focusIndex(existingChoiceIndex-1);
				}
			} else if (focusedUuid) {
				const existingChoiceIndex = this.choices.findIndex((c) => c.uuid === focusedUuid);
				let nextIndex = existingChoiceIndex - 1;
				if (existingChoiceIndex < 0) {
					nextIndex = this.choices.length - 1;
				}

				this.focusIndex(nextIndex);
			}
		} else if (event.key === 'ArrowDown') {
			event.stopPropagation();
			event.preventDefault();

			if (shiftPressed && focusedUuid) {
				const existingChoiceIndex = this.choices.findIndex((c) => c.uuid === focusedUuid);
				if (existingChoiceIndex < this.choices.length - 1) {
					this.moveChoice(focusedUuid, existingChoiceIndex+1)
					this.focusIndex(existingChoiceIndex+1);
				}
			} else if (focusedUuid) {
				const existingChoiceIndex = this.choices.findIndex((c) => c.uuid === focusedUuid);
				let nextIndex = existingChoiceIndex + 1;
				if (existingChoiceIndex > this.choices.length - 1) {
					nextIndex = 0;
				}

				this.focusIndex(nextIndex);
			}
		} else if (event.key === 'Enter' || event.key === ' ') {
			event.stopPropagation();
			event.preventDefault();

			if (focusedUuid) {
				this.selectChoice(focusedUuid, true);
			}
		}
	}

	focusIndex(index: number): void {
		const choice = this.choices[index];
		if (choice) {
			((this.$refs.container as HTMLFieldSetElement).querySelector(`input[type="checkbox"][id="${choice.uuid}"]`)! as HTMLInputElement).focus();
		}
	}

	focusedDomChoiceUuid(): string | null {
		const container = this.$refs.container as HTMLDivElement | undefined;

		if (container === undefined) {
			return null;
		}

		if (document.activeElement && container.contains(document.activeElement)) {
			return document.activeElement.getAttribute('id');
		}

		return null;
	}

	shouldAcceptDrop(
		sourceContainerOptions: any,
		draggable: Draggable<QuestionChoiceDragPayload>,
		payload: QuestionChoiceDragPayload
	): boolean {
		if (
			payload === null || payload === undefined
			|| payload.type === undefined
			|| payload.type !== 'question:choice'
		) {
			return false;
		}

		if (payload.questionUuid === undefined || payload.questionUuid !== this.question.uuid) {
			return false;
		}

		return true;
	}

	choiceDropped(
		sourceContainer: Container<QuestionChoiceDragPayload> | null,
		targetContainer: Container<QuestionChoiceDragPayload>,
		dropIndex: number,
		payload: QuestionChoiceDragPayload
	): void {
		this.moveChoice(payload.uuid, dropIndex);
	}

	accessorizeRendered(): void {
		const rendered = this.$refs.rendered as HTMLDivElement | undefined;

		if (rendered === undefined) {
			return;
		}

		const label = rendered.querySelector(`label[for="${this.question.uuid}"]`);

		if (label !== null) {
			const labelTail = document.createElement('div');
			labelTail.classList.add('tail');
			label.appendChild(labelTail);
			label.classList.add('question-label');

			if (this.question.required) {
				const sroRequiredIndicator = document.createElement('span');
				sroRequiredIndicator.classList.add('hdl-sr-only');
				sroRequiredIndicator.textContent = '(required)';
				labelTail.appendChild(sroRequiredIndicator);
				const requiredIndicator = document.createElement('div');
				requiredIndicator.setAttribute('aria-hidden', 'true');
				requiredIndicator.classList.add('required-indicator');
				requiredIndicator.textContent = '✱';
				labelTail.appendChild(requiredIndicator);
			}
		}
	}

	setResponse(): void {
		if (this.responseGroup) {
			let choices = [];
			let rank = 0;
			for (const choiceUuid of this.selectedChoices) {
				choices.push({
					choiceUuid, rank
				});
				rank++;
			}

			if (choices.length > 0) {
				this.responseGroup.setResponse(
					this.question.uuid,
					null,
					null,
					choices,
					null
				);
			} else {
				this.responseGroup.clearResponse(this.question.uuid);
			}
		}

		if (this.selectedChoices.length === this.choices.length && this.question.required) {
			this.clearError(SurveyResponseErrorCode.MISSING_REQUIRED);
		}
	}

	getResponse(): Array<string> | null {
		return this.response;
	}

	hasResponse(): boolean {
		return this.response !== null;
	}

	clearError(errorCode: SurveyResponseErrorCode): void {
		this.errorMap.delete(errorCode);
	}

	markError(error: SurveyResponseInputErrorType): void {
		this.errorMap.set(error.code, error.error);
	}

	get topLabel(): string {
		if (this.question === null) {
			return '';
		}

		const label = this.question.options[QuestionOptionKey.LEFT_LABEL];

		if (label === undefined) {
			return '';
		}

		return label.charValue!;
	}

	get bottomLabel(): string {
		if (this.question === null) {
			return '';
		}

		const label = this.question.options[QuestionOptionKey.RIGHT_LABEL];

		if (label === undefined) {
			return '';
		}

		return label.charValue!;
	}

	get errorMessages(): Array<string> {
		return Array.from(this.errorMap.values());
	}

	get sortedChoiceEls(): Array<HTMLDivElement> {
		return (this.$refs.choices as Array<HTMLDivElement>).sort(
			//@ts-ignore
			(a, b) => parseInt(a.getAttribute('data-index')) - parseInt(b.getAttribute('data-index'))
		);
	}

	get shuffledChoices(): Array<PublishedQuestionChoiceType> {
		if (this.question === null) {
			return [];
		}

		if (!this.question.randomizeChoices) {
			return this.question.choices;
		}

		const toShuffle: Array<PublishedQuestionChoiceType> = [...this.question.choices].filter((pqc: PublishedQuestionChoiceType) => !pqc.isPinned);
		inPlaceShuffleArray<PublishedQuestionChoiceType>(toShuffle);

		const output: Array<PublishedQuestionChoiceType> = [];

		for (const placedChoice of this.question.choices) {
			if (placedChoice.isPinned) {
				output.push(placedChoice);
			} else {
				output.push(toShuffle.pop()!);
			}
		}

		return output;
	}

	get choices(): Array<PublishedQuestionChoiceType> {
		if (this.question === null) {
			return [];
		}

		const result: Array<PublishedQuestionChoiceType> = [];
		const choices: Array<PublishedQuestionChoiceType> = this.shuffledChoices;
		const alreadyIncluded: Set<string> = new Set([...this.selectedChoices]);

		for (const selectedChoice of this.selectedChoices) {
			const foundChoice = this.choices.find((c: PublishedQuestionChoiceType) => c.uuid === selectedChoice);
			if (foundChoice) {
				result.push(foundChoice);
			}
		}

		return result.concat(choices.filter((c: PublishedQuestionChoiceType) => !alreadyIncluded.has(c.uuid)));
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}
}
</script>

<style lang="scss">
.hdl-ranking {
	@apply relative;

	&.has-error {
		label {
			@apply text-hdl-wc-red;
		}
	}

	.errors {
		@apply text-hdl-wc-red text-sm;

		ul {
			list-style: none;
			margin: 0;
			padding: 0;

			li {
				margin: 0;
				list-style-type: none;
			}
		}
	}

	.scale-label {
		&.top {
			@apply mb-2;
		}
		color: var(--srv-muted);
	}

	.choices {
		.choice {
			@apply px-2 rounded mb-2 cursor-pointer select-none p-1 flex flex-row items-center border border-transparent;

			min-width: 100%;
			width: 100%;

			label {
				@apply font-normal cursor-pointer;
				font-size: 1rem;
			}

			&:hover {
				background: var(--srv-hover-fill);
			}

			min-height: 32px;

			.rank-indicator {
				@apply rounded p-1 text-xs flex flex-row items-center justify-center font-medium cursor-grab;

				width: 22px;
				height: 22px;

				background: var(--srv-hover-fill);
				color: var(--srv-muted);

				&.selected {
					background-color: var(--srv-accent);
					color: var(--srv-background);
				}
			}

			&:focus-within {
				@apply rounded;
				border-color: var(--srv-border);
			}

			input[type="checkbox"] {
				position: absolute;
				opacity: 0;
				width: 1px;
				height: 1px;
				margin: 0;
				padding: 0;
				pointer-events: none;
			}
		}
	}
}

.hdl-ranking-choice-dragging {
	@apply flex flex-row items-center cursor-grabbing;

	z-index: 1001;

	background: var(--srv-background);

	.rank-indicator {
		visibility: hidden;
	}

	input[type="checkbox"] {
		visibility: hidden;
	}
}

.hdl-ranking-drag-bar {
	@apply mt-2;
	background-color: var(--srv-accent);
	height: 1px;
	position: absolute;
}
</style>
